import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useActiveClinic } from "../context/ClinicContext"
import { useClinicPlanSnapshot } from "../features/subscriptions/hooks/useClinicPlanSnapshot"
import SubscriptionFeatureAccess from "../features/subscriptions/components/SubscriptionFeatureAccess"
import { getFinancialExecutiveBundle } from "../services/financeExecutiveService"

const EMPTY_AGING = { buckets: [], rows: [] }
const EMPTY_DOCTOR = { rows: [], unattributedPayments: 0 }
const EMPTY_METHOD = { totalCollected: 0, rows: [], trend: [] }

function formatMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0
  }).format(Number(value || 0))
}

function formatPercent(value) {
  const numeric = Number(value || 0)
  return `${numeric.toFixed(1)}%`
}

function formatDateOnly(value) {
  if (!value) return "-"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("es-MX")
}

function getTodayDateInput() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function getMonthStartDateInput() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  return `${y}-${m}-01`
}

function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
}

function toCsvValue(value) {
  const text = value == null ? "" : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.map(toCsvValue).join(","),
    ...rows.map((row) => row.map(toCsvValue).join(","))
  ].join("\n")

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function normalizeFeatureErrorPayload(rawError) {
  if (!rawError || typeof rawError !== "object") return null

  const payload =
    rawError?.code === "FEATURE_NOT_AVAILABLE"
      ? rawError
      : rawError?.response?.code === "FEATURE_NOT_AVAILABLE"
        ? rawError.response
        : null

  if (!payload) return null

  return {
    code: "FEATURE_NOT_AVAILABLE",
    message:
      payload?.message ||
      "Tu plan actual no incluye esta funcionalidad.",
    missingFeatures: Array.isArray(payload?.missingFeatures)
      ? payload.missingFeatures
      : [],
    recommendedPlans: Array.isArray(payload?.recommendedPlans)
      ? payload.recommendedPlans
      : [],
    currentPlan: payload?.currentPlan || null,
    clinicId: payload?.clinicId || null
  }
}

function extractRecommendedPlan(payload, feature) {
  if (!Array.isArray(payload?.recommendedPlans)) return null
  return payload.recommendedPlans.find((item) => item?.feature === feature)?.recommendedPlan || null
}

function methodLabel(method) {
  const upper = String(method || "").toUpperCase()
  if (upper === "CASH") return "Efectivo"
  if (upper === "CARD") return "Tarjeta"
  if (upper === "TRANSFER") return "Transferencia"
  if (upper === "UNKNOWN") return "Otro"
  if (!upper) return ""
  return "Otro"
}

function severityLabel(severity) {
  const upper = String(severity || "").toUpperCase()
  if (upper === "CRITICAL") return "Crítica"
  if (upper === "WARNING") return "Observación"
  return "Informativa"
}

function severityColor(severity) {
  const upper = String(severity || "").toUpperCase()
  if (upper === "CRITICAL") return "#991b1b"
  if (upper === "WARNING") return "#92400e"
  return "#1d4ed8"
}

function bucketPriority(bucket) {
  const upper = String(bucket || "").toUpperCase()
  if (upper === "91+") return "CRITICAL"
  if (upper === "61-90") return "HIGH"
  if (upper === "31-60") return "MEDIUM"
  return "LOW"
}

function priorityVisual(priority) {
  if (priority === "CRITICAL") {
    return { bg: "#fef2f2", border: "#fecaca", color: "#991b1b", label: "Crítico" }
  }
  if (priority === "HIGH") {
    return { bg: "#fffbeb", border: "#fde68a", color: "#92400e", label: "Alto" }
  }
  if (priority === "MEDIUM") {
    return { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8", label: "Medio" }
  }
  return { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534", label: "Bajo" }
}

function openPatientFinance(navigate, clinicId, patientId, focus = "RECEIVABLES") {
  if (!patientId) return

  const params = new URLSearchParams()
  params.set("tab", "FINANCE")
  params.set("focus", focus)
  if (clinicId) params.set("clinicId", clinicId)

  navigate(`/patients/${patientId}?${params.toString()}`)
}

function getBucketDisplayLabel(bucket, index) {
  const direct =
    String(bucket?.bucket || bucket?.label || bucket?.name || "")
      .trim()

  if (direct) return direct

  const fallback = ["0-30", "31-60", "61-90", "91+"]
  return fallback[index] || "0-30"
}

function buildMethodTrendGroups(trendRows) {
  const map = new Map()

  ;(trendRows || []).forEach((item, index) => {
    const label =
      String(item?.label || item?.period || item?.date || "").trim() ||
      `Periodo ${index + 1}`

    if (!map.has(label)) {
      map.set(label, {
        label,
        total: 0,
        count: 0,
        methods: []
      })
    }

    const group = map.get(label)
    group.total += Number(item?.total || 0)
    group.count += Number(item?.count || 0)

    const methodName = methodLabel(item?.method)
    if (methodName) {
      group.methods.push({
        method: methodName,
        total: Number(item?.total || 0),
        count: Number(item?.count || 0)
      })
    }
  })

  return Array.from(map.values())
}

export default function FinancialExecutivePage() {
  const navigate = useNavigate()
  const { activeClinicId } = useActiveClinic()
  const location = useLocation()

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  const queryClinicId = queryParams.get("clinicId") || ""
  const clinicId = queryClinicId || activeClinicId || ""
  const effectiveClinicId = clinicId && clinicId !== "ALL" ? clinicId : ""

  const { data: planSnapshot } = useClinicPlanSnapshot(effectiveClinicId)
  const plan = planSnapshot?.plan || null
  const currentPlanName = plan?.name || "Tu plan actual"
  const canFinancialExecutiveByPlan = plan ? Boolean(plan.allowAdvancedReports) : true

  const [dateFrom, setDateFrom] = useState(getMonthStartDateInput())
  const [dateTo, setDateTo] = useState(getTodayDateInput())

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [featureLock, setFeatureLock] = useState(null)

  const [summary, setSummary] = useState(null)
  const [topDebtors, setTopDebtors] = useState([])
  const [creditBalance, setCreditBalance] = useState(null)
  const [aging, setAging] = useState(EMPTY_AGING)
  const [productionVsCollection, setProductionVsCollection] = useState([])
  const [byDoctor, setByDoctor] = useState(EMPTY_DOCTOR)
  const [byMethod, setByMethod] = useState(EMPTY_METHOD)
  const [alerts, setAlerts] = useState([])

  const [activeTab, setActiveTab] = useState("SUMMARY")
  const [viewMode, setViewMode] = useState("CHARTS")
  const [search, setSearch] = useState("")

  const effectiveCanFinancialExecutive =
    canFinancialExecutiveByPlan && !featureLock

  const openUpgrade = (featureKey = "financialExecutive") =>
    navigate(`/upgrade-plan?feature=${encodeURIComponent(featureKey)}`)

  const resetData = useCallback(() => {
    setSummary(null)
    setTopDebtors([])
    setCreditBalance(null)
    setAging(EMPTY_AGING)
    setProductionVsCollection([])
    setByDoctor(EMPTY_DOCTOR)
    setByMethod(EMPTY_METHOD)
    setAlerts([])
  }, [])

  const loadData = useCallback(async () => {
    if (!effectiveClinicId) {
      setError("")
      setFeatureLock(null)
      resetData()
      return
    }

    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError("La fecha inicial no puede ser mayor que la fecha final.")
      resetData()
      return
    }

    if (plan && !canFinancialExecutiveByPlan) {
      setError("")
      setFeatureLock({
        message:
          "Tu plan actual no incluye finanzas ejecutivas, cartera avanzada y reportes premium.",
        currentPlan: { name: currentPlanName },
        recommendedPlan: null
      })
      resetData()
      return
    }

    try {
      setLoading(true)
      setError("")

      const result = await getFinancialExecutiveBundle({
        clinicId: effectiveClinicId,
        dateFrom,
        dateTo
      })

      const featurePayload = normalizeFeatureErrorPayload(result)

      if (featurePayload) {
        setFeatureLock({
          message:
            featurePayload.message ||
            "Tu plan actual no incluye esta funcionalidad.",
          currentPlan: featurePayload.currentPlan || { name: currentPlanName },
          recommendedPlan:
            extractRecommendedPlan(featurePayload, "ADVANCED_REPORTS") ||
            extractRecommendedPlan(featurePayload, "EXECUTIVE_DASHBOARD") ||
            extractRecommendedPlan(featurePayload, "FINANCIAL_EXECUTIVE") ||
            null
        })
        setError("")
        resetData()
        return
      }

      setFeatureLock(null)
      setSummary(result?.summary || null)
      setTopDebtors(Array.isArray(result?.topDebtors) ? result.topDebtors : [])
      setCreditBalance(result?.creditBalance || null)
      setAging(
        result?.aging
          ? {
              buckets: Array.isArray(result.aging?.buckets) ? result.aging.buckets : [],
              rows: Array.isArray(result.aging?.rows) ? result.aging.rows : []
            }
          : EMPTY_AGING
      )
      setProductionVsCollection(
        Array.isArray(result?.productionVsCollection)
          ? result.productionVsCollection
          : []
      )
      setByDoctor(
        result?.byDoctor
          ? {
              rows: Array.isArray(result.byDoctor?.rows) ? result.byDoctor.rows : [],
              unattributedPayments: Number(result.byDoctor?.unattributedPayments || 0)
            }
          : EMPTY_DOCTOR
      )
      setByMethod(
        result?.byMethod
          ? {
              totalCollected: Number(result.byMethod?.totalCollected || 0),
              rows: Array.isArray(result.byMethod?.rows) ? result.byMethod.rows : [],
              trend: Array.isArray(result.byMethod?.trend) ? result.byMethod.trend : []
            }
          : EMPTY_METHOD
      )
      setAlerts(Array.isArray(result?.alerts) ? result.alerts : [])
    } catch (err) {
      const featurePayload = normalizeFeatureErrorPayload(err)

      if (featurePayload) {
        setFeatureLock({
          message:
            featurePayload.message ||
            "Tu plan actual no incluye esta funcionalidad.",
          currentPlan: featurePayload.currentPlan || { name: currentPlanName },
          recommendedPlan:
            extractRecommendedPlan(featurePayload, "ADVANCED_REPORTS") ||
            extractRecommendedPlan(featurePayload, "EXECUTIVE_DASHBOARD") ||
            extractRecommendedPlan(featurePayload, "FINANCIAL_EXECUTIVE") ||
            null
        })
        setError("")
        resetData()
        return
      }

      setFeatureLock(null)
      resetData()
      setError(err?.message || "No se pudo cargar el reporte ejecutivo.")
    } finally {
      setLoading(false)
    }
  }, [
    effectiveClinicId,
    dateFrom,
    dateTo,
    resetData,
    plan,
    canFinancialExecutiveByPlan,
    currentPlanName
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  const totalCollected = Number(
    summary?.collected ??
      summary?.totalCollected ??
      byMethod?.totalCollected ??
      0
  )

  const totalProduced = Number(
    summary?.produced ??
      summary?.totalProduced ??
      summary?.production ??
      0
  )

  const receivablesPending = Number(
    summary?.receivablesPending ??
      summary?.pendingPortfolio ??
      summary?.portfolioPending ??
      0
  )

  const averageTicket = useMemo(() => {
    if (summary?.averageTicket != null) return Number(summary.averageTicket)
    const totalCount = (byMethod?.rows || []).reduce(
      (sum, item) => sum + Number(item.count || 0),
      0
    )
    if (!totalCount) return 0
    return totalCollected / totalCount
  }, [summary, byMethod, totalCollected])

  const collectionEfficiency = useMemo(() => {
    if (summary?.collectionEfficiency != null) return Number(summary.collectionEfficiency)
    if (!totalProduced) return 0
    return (totalCollected / totalProduced) * 100
  }, [summary, totalProduced, totalCollected])

  const criticalAlerts = useMemo(
    () => alerts.filter((item) => String(item.severity || "").toUpperCase() === "CRITICAL"),
    [alerts]
  )

  const warningAlerts = useMemo(
    () => alerts.filter((item) => String(item.severity || "").toUpperCase() === "WARNING"),
    [alerts]
  )

  const bucketCards = useMemo(
    () =>
      (aging?.buckets || []).map((item, index) => ({
        label: getBucketDisplayLabel(item, index),
        total: Number(item.total || 0),
        count: Number(item.count || 0),
        priority: bucketPriority(getBucketDisplayLabel(item, index))
      })),
    [aging]
  )

  const filteredDebtors = useMemo(() => {
    const q = normalizeText(search)
    return topDebtors.filter((item) => {
      if (!q) return true
      return normalizeText(item.patientName).includes(q)
    })
  }, [topDebtors, search])

  const filteredCredits = useMemo(() => {
    const q = normalizeText(search)
    const rows = creditBalance?.rows || []
    return rows.filter((item) => {
      if (!q) return true
      return normalizeText(item.patientName).includes(q)
    })
  }, [creditBalance, search])

  const filteredAgingRows = useMemo(() => {
    const q = normalizeText(search)
    return (aging?.rows || []).filter((item) => {
      if (!q) return true
      return [
        item.patientName,
        item.concept,
        item.bucket
      ]
        .filter(Boolean)
        .map(normalizeText)
        .join(" ")
        .includes(q)
    })
  }, [aging, search])

  const filteredDoctorRows = useMemo(() => {
    const q = normalizeText(search)
    return (byDoctor?.rows || []).filter((item) => {
      if (!q) return true
      return normalizeText(item.doctorName).includes(q)
    })
  }, [byDoctor, search])

  const filteredMethodRows = useMemo(() => {
    const q = normalizeText(search)
    return (byMethod?.rows || []).filter((item) => {
      if (!q) return true
      return normalizeText(methodLabel(item.method)).includes(q)
    })
  }, [byMethod, search])

  const methodTrendGroups = useMemo(
    () => buildMethodTrendGroups(byMethod?.trend || []),
    [byMethod]
  )

  const exportCurrentTabCsv = () => {
    if (!effectiveCanFinancialExecutive) {
      openUpgrade("financialExecutive")
      return
    }

    if (activeTab === "SUMMARY") {
      downloadCsv(
        `finanzas_resumen_${dateFrom}_${dateTo}.csv`,
        ["Métrica", "Valor"],
        [
          ["Cobrado", totalCollected],
          ["Producido", totalProduced],
          ["Cartera pendiente", receivablesPending],
          ["Ticket promedio", averageTicket],
          ["Eficiencia de cobranza %", collectionEfficiency],
          ["Alertas críticas", criticalAlerts.length],
          ["Alertas de observación", warningAlerts.length]
        ]
      )
      return
    }

    if (activeTab === "COLLECTION") {
      downloadCsv(
        `finanzas_cobranza_${dateFrom}_${dateTo}.csv`,
        ["Paciente", "Concepto", "Saldo", "Días", "Bucket"],
        filteredAgingRows.map((item) => [
          item.patientName || "Paciente",
          item.concept || "Cuenta por cobrar",
          item.balance || 0,
          item.ageDays || 0,
          item.bucket || "-"
        ])
      )
      return
    }

    if (activeTab === "PRODUCTION") {
      downloadCsv(
        `finanzas_produccion_${dateFrom}_${dateTo}.csv`,
        ["Doctor", "Producción", "Cobrado", "Cartera", "Eficiencia %", "Tratamientos", "Pagos"],
        filteredDoctorRows.map((item) => [
          item.doctorName || "Sin atribución",
          item.production || 0,
          item.collected || 0,
          item.pendingPortfolio || 0,
          item.collectionEfficiency || 0,
          item.treatmentsCount || 0,
          item.paymentsCount || 0
        ])
      )
      return
    }

    downloadCsv(
      `finanzas_metodos_${dateFrom}_${dateTo}.csv`,
      ["Método", "Total", "Pagos", "Ticket promedio", "Participación %"],
      filteredMethodRows.map((item) => [
        methodLabel(item.method),
        item.total || 0,
        item.count || 0,
        item.averageTicket || 0,
        item.sharePercent || 0
      ])
    )
  }

  const printReport = () => {
    window.print()
  }

  return (
    <div style={{ padding: 20, background: "#f8fafc", minHeight: "100vh", display: "grid", gap: 16 }}>
      <style>{`
        @media print {
          button {
            display: none !important;
          }
          body {
            background: white !important;
          }
        }
      `}</style>

      <HeroCard
        title="Finanzas ejecutivas"
        subtitle="Lectura gerencial de producción, cobranza, cartera y riesgo sin mezclarla con la operación diaria."
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={printReport}
              style={{
                ...buttonStyle,
                background: "#ffffff"
              }}
            >
              Imprimir
            </button>

            <button
              onClick={exportCurrentTabCsv}
              disabled={!effectiveCanFinancialExecutive}
              style={{
                ...buttonStyle,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                color: "#1d4ed8",
                opacity: effectiveCanFinancialExecutive ? 1 : 0.7,
                cursor: effectiveCanFinancialExecutive ? "pointer" : "not-allowed"
              }}
            >
              Exportar vista
            </button>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(220px, 260px) minmax(220px, 260px) repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12
            }}
          >
            <div>
              <FieldLabel>Desde</FieldLabel>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <FieldLabel>Hasta</FieldLabel>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={inputStyle}
              />
            </div>

            <HeroMetric label="Cobrado" value={formatMoney(totalCollected)} />
            <HeroMetric label="Producido" value={formatMoney(totalProduced)} />
            <HeroMetric label="Cartera pendiente" value={formatMoney(receivablesPending)} tone="warning" />
            <HeroMetric label="Ticket promedio" value={formatMoney(averageTicket)} />
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 16,
              padding: 14
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) auto",
                gap: 12,
                alignItems: "center"
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: "#64748b",
                    textTransform: "uppercase",
                    fontWeight: 900,
                    letterSpacing: 0.35
                  }}
                >
                  Lectura rápida
                </div>

                <div
                  style={{
                    marginTop: 6,
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#0f172a",
                    letterSpacing: "-0.02em"
                  }}
                >
                  {receivablesPending > totalCollected
                    ? "La cartera pendiente ya pesa más que lo cobrado del periodo."
                    : collectionEfficiency < 70
                      ? "La eficiencia de cobranza está baja para el nivel producido."
                      : "La lectura general del periodo es estable."}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 13,
                    color: "#64748b",
                    lineHeight: 1.45
                  }}
                >
                  Eficiencia actual: <strong>{formatPercent(collectionEfficiency)}</strong> ·
                  Alertas críticas: <strong>{criticalAlerts.length}</strong> ·
                  Observaciones: <strong>{warningAlerts.length}</strong>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <TabChip
                  active={viewMode === "CHARTS"}
                  label="Vista gráfica"
                  onClick={() => setViewMode("CHARTS")}
                />
                <TabChip
                  active={viewMode === "TABLE"}
                  label="Vista de tabla"
                  onClick={() => setViewMode("TABLE")}
                />
              </div>
            </div>
          </div>
        </div>
      </HeroCard>

      {!effectiveClinicId ? (
        <StateCard tone="danger" title="Sin clínica activa">
          No hay una clínica válida seleccionada para consultar finanzas ejecutivas.
        </StateCard>
      ) : null}

      <SectionCard
        title="Vista ejecutiva"
        subtitle="Navega por bloques gerenciales claros, primero en formato gráfico y luego en tabla si necesitas más detalle."
      >
        <div style={{ display: "grid", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              gap: 12,
              alignItems: "end"
            }}
          >
            <div>
              <FieldLabel>Buscar paciente, doctor, método o concepto</FieldLabel>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Ej. Juan, tarjeta, ortodoncia..."
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <TabChip active={activeTab === "SUMMARY"} label="Resumen" onClick={() => setActiveTab("SUMMARY")} />
              <TabChip active={activeTab === "COLLECTION"} label="Cobranza" onClick={() => setActiveTab("COLLECTION")} />
              <TabChip active={activeTab === "PRODUCTION"} label="Producción" onClick={() => setActiveTab("PRODUCTION")} />
              <TabChip active={activeTab === "METHODS"} label="Métodos" onClick={() => setActiveTab("METHODS")} />
            </div>
          </div>

          {!effectiveCanFinancialExecutive ? (
            <SubscriptionFeatureAccess
              enabled={false}
              featureKey="financialExecutive"
              planName={currentPlanName}
              subtitle="Desbloquea finanzas ejecutivas, cartera avanzada, desempeño por doctor, métodos y alertas premium."
              onUpgrade={() => openUpgrade("financialExecutive")}
              minHeight={300}
            />
          ) : loading ? (
            <StateLine text="Cargando reporte ejecutivo..." />
          ) : error ? (
            <StateCard tone="danger" title="No se pudo cargar el reporte">
              {error}
            </StateCard>
          ) : (
            <>
              {activeTab === "SUMMARY" && viewMode === "CHARTS" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(340px, 0.95fr)",
                      gap: 16,
                      alignItems: "start"
                    }}
                  >
                    <SectionSubCard
                      title="Comparativo del periodo"
                      subtitle="Vista rápida de magnitudes clave."
                    >
                      <MetricBars
                        items={[
                          { label: "Cobrado", value: totalCollected, tone: "blue" },
                          { label: "Producido", value: totalProduced, tone: "slate" },
                          { label: "Cartera", value: receivablesPending, tone: "amber" },
                          { label: "Saldo a favor", value: Number(creditBalance?.totalCreditBalance || 0), tone: "green" }
                        ]}
                        formatter={formatMoney}
                      />
                    </SectionSubCard>

                    <SectionSubCard
                      title="Alertas"
                      subtitle="Riesgos y observaciones prioritarias."
                    >
                      {!alerts.length ? (
                        <EmptyState text="Sin alertas visibles para este rango." />
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {alerts.slice(0, 4).map((item, idx) => (
                            <AlertCard
                              key={`${item.type || "alert"}-${idx}`}
                              title={item.title || item.type || "Alerta"}
                              message={item.message || "Sin detalle"}
                              severity={item.severity}
                            />
                          ))}
                        </div>
                      )}
                    </SectionSubCard>
                  </div>

                  <SectionSubCard
                    title="Top deudores"
                    subtitle="Pacientes con mayor cartera pendiente."
                  >
                    {filteredDebtors.length ? (
                      <MetricBars
                        items={filteredDebtors.slice(0, 8).map((item, index) => ({
                          label: item.patientName || "Paciente",
                          value: Number(item.totalPending || 0),
                          helper: `${item.receivablesCount || 0} CxC · ${item.oldestDebtDays || 0} días`,
                          tone: "amber",
                          onClick: () =>
                            openPatientFinance(navigate, effectiveClinicId, item.patientId, "RECEIVABLES"),
                          key: `debtor-${item.patientId || index}`
                        }))}
                        formatter={formatMoney}
                        clickable
                      />
                    ) : (
                      <EmptyState text="Sin deudores visibles en el rango." />
                    )}
                  </SectionSubCard>
                </div>
              ) : null}

              {activeTab === "SUMMARY" && viewMode === "TABLE" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="KPIs del periodo"
                    subtitle="Lectura sintetizada del negocio."
                  >
                    <SimpleTable
                      headers={["Indicador", "Valor"]}
                      rows={[
                        ["Cobrado", formatMoney(totalCollected)],
                        ["Producido", formatMoney(totalProduced)],
                        ["Cartera pendiente", formatMoney(receivablesPending)],
                        ["Ticket promedio", formatMoney(averageTicket)],
                        ["Eficiencia", formatPercent(collectionEfficiency)],
                        ["Saldo a favor", formatMoney(creditBalance?.totalCreditBalance || 0)],
                        ["Alertas críticas", String(criticalAlerts.length)],
                        ["Observaciones", String(warningAlerts.length)]
                      ]}
                      emptyLabel="Sin indicadores para mostrar."
                    />
                  </SectionSubCard>

                  <SectionSubCard
                    title="Top deudores"
                    subtitle="Seguimiento ejecutivo."
                  >
                    <SimpleTable
                      headers={["Paciente", "Pendiente", "CxC", "Días", "Acciones"]}
                      rows={filteredDebtors.slice(0, 8).map((item, index) => [
                        <div key={`name-${item.patientId || index}`}>
                          <div style={{ fontWeight: 900, color: "#0f172a" }}>{item.patientName || "Paciente"}</div>
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                            {formatDateOnly(item.oldestDebtDate)}
                          </div>
                        </div>,
                        formatMoney(item.totalPending || 0),
                        String(item.receivablesCount || 0),
                        String(item.oldestDebtDays || 0),
                        <ActionWrap key={`actions-${item.patientId || index}`}>
                          <MiniActionButton
                            label="Paciente"
                            tone="info"
                            onClick={() => openPatientFinance(navigate, effectiveClinicId, item.patientId, "FINANCE")}
                          />
                        </ActionWrap>
                      ])}
                      emptyLabel="Sin deudores visibles en el rango."
                    />
                  </SectionSubCard>
                </div>
              ) : null}

              {activeTab === "COLLECTION" && viewMode === "CHARTS" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Tramos de antigüedad"
                    subtitle="Concentración de cartera por tramo."
                  >
                    {(bucketCards || []).some((item) => item.total > 0) ? (
                      <MetricBars
                        items={bucketCards
                          .filter((item) => item.total > 0)
                          .map((item, index) => ({
                            label: `Tramo ${item.label}`,
                            value: item.total,
                            helper: `${item.count} registro(s)`,
                            tone:
                              item.priority === "CRITICAL"
                                ? "red"
                                : item.priority === "HIGH"
                                  ? "amber"
                                  : item.priority === "MEDIUM"
                                    ? "blue"
                                    : "green",
                            key: `bucket-${item.label}-${index}`
                          }))}
                        formatter={formatMoney}
                      />
                    ) : (
                      <EmptyState text="Sin tramos con monto en el rango." />
                    )}
                  </SectionSubCard>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                      gap: 16,
                      alignItems: "start"
                    }}
                  >
                    <SectionSubCard
                      title="Cartera más vieja"
                      subtitle="Lo primero que debería atacarse."
                    >
                      {filteredAgingRows.length ? (
                        <MetricBars
                          items={filteredAgingRows.slice(0, 8).map((item, index) => ({
                            label: item.patientName || "Paciente",
                            value: Number(item.balance || 0),
                            helper: `${item.bucket || "0-30"} · ${item.ageDays || 0} días`,
                            tone:
                              bucketPriority(item.bucket) === "CRITICAL"
                                ? "red"
                                : bucketPriority(item.bucket) === "HIGH"
                                  ? "amber"
                                  : "blue",
                            onClick: () =>
                              openPatientFinance(navigate, effectiveClinicId, item.patientId, "RECEIVABLES"),
                            key: `aging-${item.patientId || index}`
                          }))}
                          formatter={formatMoney}
                          clickable
                        />
                      ) : (
                        <EmptyState text="Sin cartera vencida visible." />
                      )}
                    </SectionSubCard>

                    <SectionSubCard
                      title="Saldo a favor"
                      subtitle="Pacientes con crédito disponible."
                    >
                      {filteredCredits.length ? (
                        <MetricBars
                          items={filteredCredits.slice(0, 8).map((item, index) => ({
                            label: item.patientName || "Paciente",
                            value: Number(item.totalCredit || 0),
                            tone: "green",
                            onClick: () =>
                              openPatientFinance(navigate, effectiveClinicId, item.patientId, "MANUAL_APPLY"),
                            key: `credit-${item.patientId || index}`
                          }))}
                          formatter={formatMoney}
                          clickable
                        />
                      ) : (
                        <EmptyState text="Sin saldos a favor en el rango." />
                      )}
                    </SectionSubCard>
                  </div>
                </div>
              ) : null}

              {activeTab === "COLLECTION" && viewMode === "TABLE" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Detalle de antigüedad"
                    subtitle="Cartera ordenada por vencimiento."
                  >
                    <SimpleTable
                      headers={["Paciente", "Concepto", "Saldo", "Días", "Tramo", "Acciones"]}
                      rows={filteredAgingRows.map((item, index) => [
                        item.patientName || "Paciente",
                        item.concept || "Cuenta por cobrar",
                        formatMoney(item.balance || 0),
                        String(item.ageDays || 0),
                        <PriorityBadge
                          key={`badge-${item.patientId || index}`}
                          priority={bucketPriority(item.bucket)}
                          label={item.bucket || "0-30"}
                        />,
                        <ActionWrap key={`actions-${item.patientId || index}`}>
                          <MiniActionButton
                            label="Abrir paciente"
                            tone="info"
                            onClick={() => openPatientFinance(navigate, effectiveClinicId, item.patientId, "RECEIVABLES")}
                          />
                        </ActionWrap>
                      ])}
                      emptyLabel="Sin antigüedad de cartera visible."
                    />
                  </SectionSubCard>

                  <SectionSubCard
                    title="Saldo a favor"
                    subtitle="Pacientes con crédito disponible para aplicar."
                  >
                    <SimpleTable
                      headers={["Paciente", "Saldo a favor", "Acciones"]}
                      rows={filteredCredits.map((item, index) => [
                        item.patientName || "Paciente",
                        formatMoney(item.totalCredit || 0),
                        <ActionWrap key={`credit-actions-${item.patientId || index}`}>
                          <MiniActionButton
                            label="Aplicar saldo"
                            tone="info"
                            onClick={() => openPatientFinance(navigate, effectiveClinicId, item.patientId, "MANUAL_APPLY")}
                          />
                        </ActionWrap>
                      ])}
                      emptyLabel="Sin saldos a favor en el rango."
                    />
                  </SectionSubCard>
                </div>
              ) : null}

              {activeTab === "PRODUCTION" && viewMode === "CHARTS" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Producción vs cobro"
                    subtitle="Lectura temporal del periodo."
                  >
                    {productionVsCollection.length ? (
                      <DualMetricBars
                        items={productionVsCollection.map((item, index) => ({
                          label: item.label || item.period || `Periodo ${index + 1}`,
                          primaryLabel: "Producido",
                          primaryValue: Number(item.production || 0),
                          secondaryLabel: "Cobrado",
                          secondaryValue: Number(item.collected || 0)
                        }))}
                        formatter={formatMoney}
                      />
                    ) : (
                      <EmptyState text="Sin periodos comparables en el rango." />
                    )}
                  </SectionSubCard>

                  <SectionSubCard
                    title="Desempeño por doctor"
                    subtitle="Producción, cobranza y cartera atribuida."
                  >
                    <DoctorPerformanceCards
                      rows={filteredDoctorRows}
                      formatter={formatMoney}
                    />
                  </SectionSubCard>

                  {Number(byDoctor?.unattributedPayments || 0) > 0 ? (
                    <StateCard tone="neutral" title="Pagos sin atribución">
                      Hay <strong>{formatMoney(byDoctor.unattributedPayments)}</strong> registrados sin doctor atribuible.
                    </StateCard>
                  ) : null}
                </div>
              ) : null}

              {activeTab === "PRODUCTION" && viewMode === "TABLE" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Producción vs cobro"
                    subtitle="Lectura temporal del periodo."
                  >
                    <SimpleTable
                      headers={["Periodo", "Producido", "Cobrado", "Diferencia"]}
                      rows={productionVsCollection.map((item, index) => [
                        item.label || item.period || `Periodo ${index + 1}`,
                        formatMoney(item.production || 0),
                        formatMoney(item.collected || 0),
                        formatMoney(Number(item.collected || 0) - Number(item.production || 0))
                      ])}
                      emptyLabel="Sin periodos comparables en el rango."
                    />
                  </SectionSubCard>

                  <SectionSubCard
                    title="Desempeño por doctor"
                    subtitle="Producción, cobranza atribuida y eficiencia."
                  >
                    <SimpleTable
                      headers={["Doctor", "Producción", "Cobrado", "Cartera", "Eficiencia", "Tratamientos", "Pagos"]}
                      rows={filteredDoctorRows.map((item, index) => [
                        item.doctorName || "Sin atribución",
                        formatMoney(item.production || 0),
                        formatMoney(item.collected || 0),
                        formatMoney(item.pendingPortfolio || 0),
                        formatPercent(item.collectionEfficiency || 0),
                        String(item.treatmentsCount || 0),
                        String(item.paymentsCount || 0)
                      ])}
                      emptyLabel="Sin desempeño por doctor en el rango."
                    />
                  </SectionSubCard>

                  {Number(byDoctor?.unattributedPayments || 0) > 0 ? (
                    <StateCard tone="neutral" title="Pagos sin atribución">
                      Hay <strong>{formatMoney(byDoctor.unattributedPayments)}</strong> registrados sin doctor atribuible.
                    </StateCard>
                  ) : null}
                </div>
              ) : null}

              {activeTab === "METHODS" && viewMode === "CHARTS" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Participación por método"
                    subtitle="Cómo se distribuye la captación."
                  >
                    {filteredMethodRows.length ? (
                      <MetricBars
                        items={filteredMethodRows.map((item, index) => ({
                          label: methodLabel(item.method) || `Método ${index + 1}`,
                          value: Number(item.total || 0),
                          helper: `${formatPercent(item.sharePercent || 0)} · ${item.count || 0} pago(s)`,
                          tone:
                            String(item.method || "").toUpperCase() === "CASH"
                              ? "green"
                              : String(item.method || "").toUpperCase() === "CARD"
                                ? "blue"
                                : "slate",
                          key: `method-${item.method || index}`
                        }))}
                        formatter={formatMoney}
                      />
                    ) : (
                      <EmptyState text="Sin métodos visibles en el rango." />
                    )}
                  </SectionSubCard>

                  <SectionSubCard
                    title="Tendencia por método"
                    subtitle="Comportamiento temporal agrupado."
                  >
                    <MethodTrendCards groups={methodTrendGroups} formatter={formatMoney} />
                  </SectionSubCard>
                </div>
              ) : null}

              {activeTab === "METHODS" && viewMode === "TABLE" ? (
                <div style={{ display: "grid", gap: 16 }}>
                  <SectionSubCard
                    title="Desglose por método"
                    subtitle="Participación, ticket promedio y volumen."
                  >
                    <SimpleTable
                      headers={["Método", "Total", "Pagos", "Ticket promedio", "Participación"]}
                      rows={filteredMethodRows.map((item, index) => [
                        methodLabel(item.method) || `Método ${index + 1}`,
                        formatMoney(item.total || 0),
                        String(item.count || 0),
                        formatMoney(item.averageTicket || 0),
                        formatPercent(item.sharePercent || 0)
                      ])}
                      emptyLabel="Sin métodos visibles en el rango."
                    />
                  </SectionSubCard>

                  <SectionSubCard
                    title="Tendencia por método"
                    subtitle="Comportamiento temporal del periodo."
                  >
                    <SimpleTable
                      headers={["Periodo", "Total", "Pagos", "Métodos"]}
                      rows={methodTrendGroups.map((group, index) => [
                        group.label,
                        formatMoney(group.total || 0),
                        String(group.count || 0),
                        group.methods.length
                          ? group.methods.map((item) => item.method).join(", ")
                          : "Sin desglose por método"
                      ])}
                      emptyLabel="Sin tendencia por método en el rango."
                    />
                  </SectionSubCard>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  )
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#fff",
  boxSizing: "border-box"
}

const buttonStyle = {
  minHeight: 40,
  padding: "9px 13px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer",
  background: "#ffffff",
  color: "#0f172a",
  boxShadow: "0 6px 18px rgba(15,23,42,0.04)"
}

function HeroCard({ title, subtitle, actions, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
        borderRadius: 20,
        padding: 18,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        display: "grid",
        gap: 16
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 14,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div>
          <div
            style={{
              display: "inline-flex",
              padding: "6px 10px",
              borderRadius: 999,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              color: "#1d4ed8",
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: 0.45,
              textTransform: "uppercase",
              marginBottom: 10
            }}
          >
            Lectura gerencial
          </div>

          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-0.04em",
              lineHeight: 1.02
            }}
          >
            {title}
          </div>

          <div
            style={{
              color: "#64748b",
              marginTop: 6,
              fontSize: 13,
              lineHeight: 1.4,
              maxWidth: 760
            }}
          >
            {subtitle}
          </div>
        </div>

        {actions}
      </div>

      {children}
    </div>
  )
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 18,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em"
          }}
        >
          {title}
        </div>

        {subtitle ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.4
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>

      {children}
    </div>
  )
}

function SectionSubCard({ title, subtitle, children }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        borderRadius: 16,
        padding: 14,
        display: "grid",
        gap: 12
      }}
    >
      <div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 900,
            color: "#0f172a",
            letterSpacing: "-0.02em"
          }}
        >
          {title}
        </div>
        {subtitle ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#64748b",
              marginTop: 4,
              lineHeight: 1.4
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function HeroMetric({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", value: "#0f172a" },
    warning: { bg: "#fffbeb", border: "#fde68a", value: "#92400e" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        borderRadius: 16,
        padding: 14
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "#64748b",
          textTransform: "uppercase",
          fontWeight: 900,
          letterSpacing: 0.35
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 22,
          fontWeight: 900,
          color: visual.value,
          lineHeight: 1.05
        }}
      >
        {value}
      </div>
    </div>
  )
}

function TabChip({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 36,
        padding: "8px 12px",
        borderRadius: 999,
        border: active ? "1px solid #1d4ed8" : "1px solid #cbd5e1",
        background: active ? "#1d4ed8" : "#ffffff",
        color: active ? "#ffffff" : "#334155",
        cursor: "pointer",
        fontWeight: 900,
        fontSize: 12
      }}
    >
      {label}
    </button>
  )
}

function PriorityBadge({ priority, label }) {
  const visual = priorityVisual(priority)

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 30,
        padding: "4px 9px",
        borderRadius: 999,
        background: visual.bg,
        border: `1px solid ${visual.border}`,
        color: visual.color,
        fontSize: 12,
        fontWeight: 900
      }}
    >
      {label || visual.label}
    </span>
  )
}

function ActionWrap({ children }) {
  return <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>
}

function MiniActionButton({ label, onClick, tone = "neutral" }) {
  const tones = {
    neutral: {
      bg: "#ffffff",
      border: "#cbd5e1",
      color: "#334155"
    },
    info: {
      bg: "#eff6ff",
      border: "#bfdbfe",
      color: "#1d4ed8"
    }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <button
      onClick={onClick}
      style={{
        minHeight: 34,
        padding: "7px 10px",
        borderRadius: 10,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        cursor: "pointer",
        fontWeight: 800,
        fontSize: 12
      }}
    >
      {label}
    </button>
  )
}

function FieldLabel({ children }) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: 6,
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800
      }}
    >
      {children}
    </label>
  )
}

function StateCard({ tone = "neutral", title, children }) {
  const tones = {
    neutral: { bg: "#ffffff", border: "#e2e8f0", color: "#334155" },
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" }
  }

  const visual = tones[tone] || tones.neutral

  return (
    <div
      style={{
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 8px 22px rgba(15,23,42,0.04)"
      }}
    >
      <div style={{ fontWeight: 900, marginBottom: 4 }}>{title}</div>
      <div style={{ lineHeight: 1.45 }}>{children}</div>
    </div>
  )
}

function StateLine({ text }) {
  return <div style={{ color: "#64748b" }}>{text}</div>
}

function EmptyState({ text }) {
  return <div style={{ color: "#64748b", fontSize: 13 }}>{text}</div>
}

function AlertCard({ title, message, severity }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        borderRadius: 14,
        padding: 12,
        display: "grid",
        gap: 6
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap"
        }}
      >
        <div style={{ fontWeight: 900, color: "#0f172a" }}>{title}</div>
        <span
          style={{
            color: severityColor(severity),
            fontWeight: 900,
            fontSize: 12
          }}
        >
          {severityLabel(severity)}
        </span>
      </div>
      <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.4 }}>{message}</div>
    </div>
  )
}

function MetricBars({ items, formatter, clickable = false }) {
  const max = Math.max(...items.map((item) => Number(item.value || 0)), 1)

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {items.map((item, index) => {
        const percent = (Number(item.value || 0) / max) * 100
        const tone = chartTone(item.tone)
        const Wrapper = clickable && item.onClick ? "button" : "div"

        return (
          <Wrapper
            key={item.key || `${item.label}-${index}`}
            onClick={item.onClick}
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 14,
              padding: 12,
              display: "grid",
              gap: 8,
              cursor: clickable && item.onClick ? "pointer" : "default",
              textAlign: "left"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap"
              }}
            >
              <div style={{ fontWeight: 800, color: "#0f172a" }}>{item.label}</div>
              <div style={{ fontWeight: 900, color: tone.color }}>
                {formatter ? formatter(item.value) : item.value}
              </div>
            </div>

            <div
              style={{
                width: "100%",
                height: 10,
                borderRadius: 999,
                background: "#e2e8f0",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  width: `${Math.max(percent, 4)}%`,
                  height: "100%",
                  background: tone.fill,
                  borderRadius: 999
                }}
              />
            </div>

            {item.helper ? (
              <div style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.35 }}>
                {item.helper}
              </div>
            ) : null}
          </Wrapper>
        )
      })}
    </div>
  )
}

function DualMetricBars({ items, formatter }) {
  const max = Math.max(
    ...items.map((item) =>
      Math.max(Number(item.primaryValue || 0), Number(item.secondaryValue || 0))
    ),
    1
  )

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {items.map((item, index) => {
        const primaryPercent = (Number(item.primaryValue || 0) / max) * 100
        const secondaryPercent = (Number(item.secondaryValue || 0) / max) * 100

        return (
          <div
            key={`${item.label}-${index}`}
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 14,
              padding: 12,
              display: "grid",
              gap: 8
            }}
          >
            <div style={{ fontWeight: 800, color: "#0f172a" }}>{item.label}</div>

            <div style={{ display: "grid", gap: 6 }}>
              <DualBarRow
                label={item.primaryLabel}
                value={item.primaryValue}
                percent={primaryPercent}
                formatter={formatter}
                fill="#334155"
              />
              <DualBarRow
                label={item.secondaryLabel}
                value={item.secondaryValue}
                percent={secondaryPercent}
                formatter={formatter}
                fill="#2563eb"
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function DualBarRow({ label, value, percent, formatter, fill }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center"
        }}
      >
        <div style={{ fontSize: 12.5, color: "#64748b", fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 12.5, color: "#0f172a", fontWeight: 800 }}>
          {formatter ? formatter(value) : value}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: 9,
          borderRadius: 999,
          background: "#e2e8f0",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${Math.max(percent, 3)}%`,
            height: "100%",
            background: fill,
            borderRadius: 999
          }}
        />
      </div>
    </div>
  )
}

function DoctorPerformanceCards({ rows, formatter }) {
  if (!rows.length) {
    return <EmptyState text="Sin desempeño por doctor en el rango." />
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {rows.map((item, index) => {
        const efficiency = Number(item.collectionEfficiency || 0)

        return (
          <div
            key={`${item.doctorName || "doctor"}-${index}`}
            style={{
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              borderRadius: 14,
              padding: 12,
              display: "grid",
              gap: 10
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center"
              }}
            >
              <div style={{ fontWeight: 900, color: "#0f172a" }}>
                {item.doctorName || "Sin atribución"}
              </div>

              <PriorityBadge
                priority={
                  efficiency >= 80
                    ? "LOW"
                    : efficiency >= 60
                      ? "MEDIUM"
                      : efficiency >= 40
                        ? "HIGH"
                        : "CRITICAL"
                }
                label={`Eficiencia ${formatPercent(efficiency)}`}
              />
            </div>

            <DualMetricBars
              items={[
                {
                  label: "Resultado",
                  primaryLabel: "Producción",
                  primaryValue: Number(item.production || 0),
                  secondaryLabel: "Cobrado",
                  secondaryValue: Number(item.collected || 0)
                }
              ]}
              formatter={formatter}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 8
              }}
            >
              <MiniMetric label="Cartera" value={formatter(item.pendingPortfolio || 0)} />
              <MiniMetric label="Tratamientos" value={String(item.treatmentsCount || 0)} />
              <MiniMetric label="Pagos" value={String(item.paymentsCount || 0)} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MethodTrendCards({ groups, formatter }) {
  if (!groups.length) {
    return <EmptyState text="Sin tendencia por método en el rango." />
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {groups.map((group, index) => (
        <div
          key={`${group.label}-${index}`}
          style={{
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            borderRadius: 14,
            padding: 12,
            display: "grid",
            gap: 10
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center"
            }}
          >
            <div style={{ fontWeight: 900, color: "#0f172a" }}>{group.label}</div>
            <div style={{ fontWeight: 900, color: "#0f172a" }}>
              {formatter(group.total || 0)}
            </div>
          </div>

          <div style={{ fontSize: 12.5, color: "#64748b" }}>
            {group.count || 0} pago(s)
          </div>

          {group.methods.length ? (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {group.methods.map((item, idx) => (
                <span
                  key={`${group.label}-${item.method}-${idx}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    minHeight: 30,
                    padding: "4px 9px",
                    borderRadius: 999,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 800
                  }}
                >
                  {item.method}: {formatter(item.total || 0)}
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "#64748b" }}>
              Sin desglose por método en este periodo.
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function MiniMetric({ label, value }) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        background: "#f8fafc",
        borderRadius: 12,
        padding: 10
      }}
    >
      <div style={{ fontSize: 11.5, color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 14, color: "#0f172a", fontWeight: 900 }}>
        {value}
      </div>
    </div>
  )
}

function chartTone(tone) {
  if (tone === "red") return { fill: "#dc2626", color: "#991b1b" }
  if (tone === "amber") return { fill: "#d97706", color: "#92400e" }
  if (tone === "green") return { fill: "#16a34a", color: "#166534" }
  if (tone === "blue") return { fill: "#2563eb", color: "#1d4ed8" }
  if (tone === "slate") return { fill: "#475569", color: "#334155" }
  return { fill: "#2563eb", color: "#1d4ed8" }
}

function SimpleTable({ headers, rows, emptyLabel }) {
  if (!rows.length) {
    return (
      <div
        style={{
          color: "#64748b",
          border: "1px dashed #e2e8f0",
          borderRadius: 14,
          padding: 14,
          background: "#ffffff"
        }}
      >
        {emptyLabel}
      </div>
    )
  }

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        background: "#ffffff"
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: "left",
                  fontSize: 11.5,
                  color: "#64748b",
                  borderBottom: "1px solid #e2e8f0",
                  padding: "10px 8px",
                  whiteSpace: "nowrap",
                  background: "#f8fafc",
                  textTransform: "uppercase",
                  letterSpacing: 0.35
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => (
            <tr key={`row-${idx}`}>
              {row.map((cell, cellIdx) => (
                <td
                  key={`cell-${idx}-${cellIdx}`}
                  style={{
                    padding: "10px 8px",
                    borderBottom: "1px solid #f1f5f9",
                    color: "#334155",
                    fontSize: 14,
                    verticalAlign: "top"
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}